import React, { useState, useEffect, useRef } from "react";
import {
  Alert,
  Button,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
} from "@mui/material";

import { useData } from "../contexts/DataContext"; // Import DataContext
import { useDualMsal } from "../contexts/MsalContext"; // Import useDualMsal hook
import {
  processFailedUploads,
  FAILED_UPLOADS_KEY,
  isUploadOwnedByUser,
} from "../utilities/postData"; // Import processFailedUploads and FAILED_UPLOADS_KEY

const CachedUploadsNotification = () => {
  // Cached records are owned by the authenticated B2C user. Individual study
  // enrolments use a composite `${b2cId}_${studyId}` participant ID.
  const { currentUser } = useData();
  const { getToken } = useDualMsal(); // Use the hook to get getToken
  const currentUserId = currentUser?.oid;

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [cachedUploads, setCachedUploads] = useState([]);
  const [isRetrying, setIsRetrying] = useState(false);

  // Check for cached uploads on component mount
  useEffect(() => {
    // Only check if the authenticated user ID is available
    if (currentUserId) {
      checkForCachedUploads();
      // Re-check every 30 seconds
      const interval = setInterval(checkForCachedUploads, 30000);
      return () => clearInterval(interval);
    }
  }, [currentUserId]); // Rerun when the authenticated user changes

  // Background replay: at most once every 5 minutes while items are queued,
  // so a mid-session outage self-heals without waiting for the next login.
  // Replay itself is single-flight and sequential (see postData.js).
  const lastAutoReplayRef = useRef(0);
  const AUTO_REPLAY_INTERVAL_MS = 5 * 60 * 1000;

  // Function to check localStorage for cached uploads for the current participant
  const checkForCachedUploads = () => {
    if (!currentUserId) return;

    try {
      const allCachedData = JSON.parse(
        localStorage.getItem(FAILED_UPLOADS_KEY) || "[]"
      );
      // Filter uploads for the current participant
      const userCachedData = allCachedData.filter(
        (upload) => isUploadOwnedByUser(upload, currentUserId)
      );
      setCachedUploads(userCachedData);

      // Only show notification if the current user has cached uploads
      if (userCachedData.length > 0) {
        setOpenSnackbar(true);
        const now = Date.now();
        if (now - lastAutoReplayRef.current > AUTO_REPLAY_INTERVAL_MS && getToken) {
          lastAutoReplayRef.current = now;
          getToken()
            .then((token) => processFailedUploads(token))
            .then(() => checkForCachedUploads())
            .catch((e) => console.warn("Background replay skipped:", e?.message || e));
        }
      } else {
        setOpenSnackbar(false);
      }
    } catch (error) {
      console.error("Error checking for cached uploads:", error);
    }
  };

  // Handle retry button click
  const handleRetry = async () => {
    if (!currentUserId || !getToken) {
      // Check for getToken
      console.error(
        "Cannot retry: Missing participant ID or getToken function." // Updated error message
      );
      return;
    }
    setIsRetrying(true);
    try {
      // Acquire the current access token using getToken
      const accessToken = await getToken(); // Use getToken
      if (!accessToken) {
        throw new Error("Failed to acquire access token for retry.");
      }
      // Call processFailedUploads with the current token
      await processFailedUploads(accessToken);
      // Re-check to update the count (will automatically filter for user)
      checkForCachedUploads();
    } catch (error) {
      console.error("Error retrying uploads:", error);
      // Optionally show an error message to the user
    } finally {
      setIsRetrying(false);
      setOpenDialog(false);
    }
  };

  // Format date for display
  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  return (
    <>
      {/* Snackbar notification */}
      <Snackbar
        open={openSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="warning"
          sx={{ width: "100%" }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setOpenDialog(true)}
            >
              VIEW
            </Button>
          }
        >
          You have {cachedUploads.length} unsent study data{" "}
          {cachedUploads.length === 1 ? "item" : "items"} stored locally.
        </Alert>
      </Snackbar>

      {/* Detailed dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Pending Data Uploads</DialogTitle>
        <DialogContent>
          <Typography paragraph>
            The following study data couldn't be sent to the server and is
            stored on your device. You can retry sending it now.
          </Typography>

          <List>
            {cachedUploads.map((upload, index) => (
              <React.Fragment key={upload.id || index}>
                <ListItem>
                  <ListItemText
                    primary={
                      upload.endpoint === "add-reward-bonus"
                        ? `Performance bonus: ${
                            upload.data?.sessionCode || "Unknown session"
                          } - ${upload.data?.taskParam || "Unknown task"}`
                        : `${
                            upload.data?.session || "Unknown session"
                          } - ${upload.data?.module || "Unknown module"}`
                    }
                    secondary={`From: ${formatDate(upload.timestamp)}`}
                  />
                </ListItem>
                {index < cachedUploads.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="primary">
            Close
          </Button>
          <Button
            onClick={handleRetry}
            color="primary"
            variant="contained"
            disabled={isRetrying}
          >
            {isRetrying ? "Retrying..." : "Retry All Now"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CachedUploadsNotification;
